import { getFrontendSocket } from './frontendSocket';

export type SubscribeAck = { subscribed: string[]; rejected: string[] };

const refCounts = new Map<string, number>();

function getSocket() {
  const socket = getFrontendSocket();
  if (!socket) {
    throw new Error(
      'No active frontend socket. Ensure acquireFrontendSocket() was called before using subscriptions.',
    );
  }
  return socket;
}

export function subscribeDevices(
  deviceIds: string[],
  onSettled?: (ack: SubscribeAck) => void,
): void {
  const toEmit: string[] = [];

  for (const id of deviceIds) {
    const count = refCounts.get(id) ?? 0;
    refCounts.set(id, count + 1);
    if (count === 0) {
      toEmit.push(id);
    }
  }

  if (toEmit.length > 0) {
    getSocket().emit('subscribe_devices', { deviceIds: toEmit }, (ack: SubscribeAck) => {
      if (ack?.rejected?.length) {
        // Clear rejected IDs entirely so subsequent subscribes can retry
        for (const id of ack.rejected) {
          refCounts.delete(id);
        }
      }

      onSettled?.(ack);
    });
    return;
  }

  onSettled?.({ subscribed: [], rejected: [] });
}

/** Returns the ids whose last subscriber just left, so the caller can release per-device state. */
export function unsubscribeDevices(deviceIds: string[]): string[] {
  const toEmit: string[] = [];

  for (const id of deviceIds) {
    const count = refCounts.get(id) ?? 0;
    if (count <= 1) {
      refCounts.delete(id);
      toEmit.push(id);
    } else {
      refCounts.set(id, count - 1);
    }
  }

  if (toEmit.length > 0) {
    const socket = getFrontendSocket();
    socket?.emit('unsubscribe_devices', { deviceIds: toEmit });
  }

  return toEmit;
}

/**
 * A subscribe snapshot is emitted after an async server read, so it can land after the last
 * subscriber left. The frame carries no subscription generation, so its recipients test this.
 */
export function isDeviceSubscribed(deviceId: string): boolean {
  return refCounts.has(deviceId);
}

export function resubscribeAll(): void {
  const ids = Array.from(refCounts.keys());
  if (ids.length === 0) return;
  const socket = getFrontendSocket();
  if (!socket) return;
  socket.emit('subscribe_devices', { deviceIds: ids }, (ack: SubscribeAck) => {
    if (ack?.rejected?.length) {
      for (const id of ack.rejected) {
        refCounts.delete(id);
      }
    }
  });
}

export function clearSubscriptions(): void {
  refCounts.clear();
}
